const SOCKET_URL =
  process.env.REACT_APP_BBMS_SOCKET_URL ||
  (process.env.REACT_APP_BBMS_BASE_API || "http://localhost:3397").replace(
    /\/api\/access.*$/,
    ""
  );

let socket = null;
// The guard inside initSocket only runs before its first await, so three
// callers starting together each got past it and built their own socket:
// one joined the account room, another held the listener, and nothing was
// ever delivered. Hold the in-flight promise so they all wait on one.
let connecting = null;

const loadIo = () =>
  new Promise((resolve) => {
    if (window.io) return resolve(window.io);
    const existing = document.querySelector('script[data-socket-io]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.io));
      existing.addEventListener("error", () => resolve(null));
      return;
    }
    const script = document.createElement("script");
    script.src = `${SOCKET_URL}/socket.io/socket.io.js`;
    script.setAttribute("data-socket-io", "1");
    script.onload = () => resolve(window.io);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

const initSocket = async () => {
  if (socket) return socket;
  if (connecting) return connecting;

  connecting = (async () => {
    const io = await loadIo();

    if (!io) return null;

    socket = io(SOCKET_URL, {
      transports: ["polling"],
      upgrade: false,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    return socket;
  })();

  try {
    return await connecting;
  } finally {
    connecting = null;
  }
};

export const joinRoom = async (accountId) => {
  if (!accountId) return;

  const s = await initSocket();

  if (!s) return;

  s.emit("join", String(accountId));

  // A dropped connection comes back in a fresh room, so the join has to be
  // repeated or the client goes quiet after the first blip.
  s.on("connect", () => s.emit("join", String(accountId)));
};

export const onNotification = async (callback) => {
  const s = await initSocket();
  if (s) s.on("notification", callback);
};

// Broadcast when a prompt stops applying - somebody answered it, or the
// thing it was asking for happened. Not room-scoped: every client holding
// that prompt has to drop it, and only the ones holding it react.
export const onNotificationResolved = async (callback) => {
  const s = await initSocket();
  if (s) s.on("notification_resolved", callback);
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};
